import { useState, useCallback } from 'react';
import throttle from 'lodash/throttle';

import { validate } from './validation';

import type {
  ClassNameDefinition,
  ValidationDefinition,
  ValidationResult,
} from './types';

type IsValid = boolean;

type ErrorMessage = string;

type ValueDidChangeCallback<InputValueType> = (
  value?: InputValueType | null | undefined,
) => void;

type UseValidationReturn<InputValueType> = [
  IsValid | null | undefined,
  ClassNameDefinition | null | undefined,
  ErrorMessage | null | undefined,
  ValueDidChangeCallback<InputValueType>,
];

/**
 * @example
 *   const inputValidation: ValidationDefinition<?string> = {
 *    validators: [{
 *      validator: textLengthLessThanOrEqualTo(100),
 *      invalidClassName: styles['text-area--invalid'],
 *      invalidErrorMessage: polyglot.t('input_validation.character_limit'),
 *    }],
 *  };
 *
 *   const [
 *    isValid,
 *    validationClassNames,
 *    errorMessage,
 *    informValidationOfValueChange,
 *    ] = useValidation<string>(inputValidation, value);
 */
export const useValidation = <InputValueType>(
  validation: ValidationDefinition<InputValueType | null | undefined>,
): UseValidationReturn<InputValueType> => {
  const [validationState, setValidationState] = useState<ValidationResult>({
    classNames: [],
  });

  const onChange: ValueDidChangeCallback<InputValueType> = useCallback(
    throttle(value => {
      const result = validate(value, validation);
      setValidationState(result);
    }, 200),
    [],
  );

  return [
    validationState.valid,
    validationState.classNames,
    validationState.message,
    onChange,
  ];
};
